import {
  Component,
  ElementRef,
  Inject,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { tagStyle } from '@utils/tag-color.util';
import { MatListModule } from '@angular/material/list';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { firstValueFrom } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectionList } from '@angular/material/list';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';

/** ダイアログオプション */
export type TagDialogOption = {
  deviceName: string;
  target?: DeviceTagViewModel;
};

/** タグのViewModel */
export type DeviceTagViewModel = {
  /** 全タグ一覧 */
  tagList: { [key: string]: string }[];
  /** デバイス関連タグ一覧 */
  deviceTagRefsList: { [key: string]: string }[];
};

/** フォームグループ */
type DeviceTagFormGroup = FormGroup<{
  /** 全タグ一覧 */
  tagList: FormControl<{ [key: string]: string }[]>;
  /** デバイス関連タグ */
  deviceTagRefsList: FormControl<{ [key: string]: string }[]>;
}>;

@Component({
  selector: 'app-tag-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTableModule,
    MatListModule,
    MatTooltipModule,
  ],
  templateUrl: './device-tag-edit-dialog.component.html',
  styleUrl: './device-tag-edit-dialog.component.scss',
})
export class DeviceTagEditDialogComponent {
  public readonly tagStyle = tagStyle;
  @ViewChild('deviceTags') deviceTags: MatSelectionList | undefined;
  @ViewChild('tags') tags: MatSelectionList | undefined;
  @ViewChild('deviceInput') deviceInput:
    | ElementRef<HTMLInputElement>
    | undefined;

  /** 更新を通知 */
  @Output() enableEdit = new EventEmitter<void>();

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<DeviceTagViewModel>([]);

  /** タグ一覧 */
  public tagList: { [key: string]: string }[];

  /** デバイス関連タグ一覧 */
  public deviceTagRefsList: { [key: string]: string }[];

  /** フォームグループ */
  public formGroup: DeviceTagFormGroup;

  public selectedDeviceTag: { [key: string]: string }[];

  public selectedTag: { [key: string]: string }[];

  public deviceName: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) options: TagDialogOption,
    private dialogRef: MatDialogRef<DeviceTagEditDialogComponent>,
    private dialog: MatDialog,
  ) {
    this.deviceName = options.deviceName || '';
    this.tagList = this.sortTagsByTagName(options.target?.tagList);
    this.deviceTagRefsList = this.sortTagsByTagName(
      options.target?.deviceTagRefsList,
    );
    this.selectedDeviceTag = [];
    this.selectedTag = [];
    this.formGroup = new FormGroup({
      tagList: new FormControl(options.target?.tagList || [], {
        nonNullable: true,
      }),
      deviceTagRefsList: new FormControl(
        options.target?.deviceTagRefsList || [],
        {
          nonNullable: true,
        },
      ),
    });
  }

  /**
   * 送信
   */
  public async submit() {
    const operation = '変更';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `タグを${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const deviceSetting: DeviceTagViewModel = {
      tagList: this.formGroup.get('tagList')?.value || [],
      deviceTagRefsList: this.formGroup.get('deviceTagRefsList')?.value || [],
    };
    this.dialogRef.close(deviceSetting);
  }

  /**
   * 選択したタグを設定情報エリアに移動する。
   * @param tagList 選択したタグ一覧
   */
  public moveToDeviceTagList(tagList: { [key: string]: string }[]) {
    for (const tag of tagList) {
      this.deviceTagRefsList.unshift(tag);
      this.tagList = this.tagList.filter((i) => i !== tag);
      this.tagList = this.tagList.sort((first, second) =>
        first['tagName'] > second['tagName'] ? 1 : -1,
      );
      this.deviceTagRefsList = this.deviceTagRefsList.sort((first, second) =>
        first['tagName'] > second['tagName'] ? 1 : -1,
      );
      this.formGroup = new FormGroup({
        tagList: new FormControl(this.tagList || [], {
          nonNullable: true,
        }),
        deviceTagRefsList: new FormControl(this.deviceTagRefsList || [], {
          nonNullable: true,
        }),
      });
    }
    this.deviceTagRefsList = this.sortTagsByTagName(this.deviceTagRefsList);
    // タグの移動が有った場合のみチェック状態をクリアする。
    if (tagList.length > 0) {
      this.selectedTag = [];
      this.selectedDeviceTag = [];
    }
    if (this.deviceTags) {
      this.deviceTags.deselectAll();
    }
    if (this.tags) {
      this.tags.deselectAll();
    }
  }

  /**
   * 選択したタグをタグ一覧エリアに移動する。
   * @param tagList 選択したタグ一覧
   */
  public moveToTagList(tagList: { [key: string]: string }[]) {
    for (const tag of tagList) {
      this.tagList.unshift(tag);
      this.deviceTagRefsList = this.deviceTagRefsList.filter((i) => i !== tag);
      this.tagList = this.tagList.sort((first, second) =>
        first['tagName'] > second['tagName'] ? 1 : -1,
      );
      this.deviceTagRefsList = this.deviceTagRefsList.sort((first, second) =>
        first['tagName'] > second['tagName'] ? 1 : -1,
      );
      this.formGroup = new FormGroup({
        tagList: new FormControl(this.tagList || [], {
          nonNullable: true,
        }),
        deviceTagRefsList: new FormControl(this.deviceTagRefsList || [], {
          nonNullable: true,
        }),
      });
    }
    this.tagList = this.sortTagsByTagName(this.tagList);
    // タグの移動が有った場合のみチェック状態をクリアする。
    if (tagList.length > 0) {
      this.selectedTag = [];
      this.selectedDeviceTag = [];
    }
    if (this.deviceTags) {
      this.deviceTags.deselectAll();
    }
    if (this.tags) {
      this.tags.deselectAll();
    }
  }

  /**
   * 選択した設定情報エリアのタグを選択済みとしてマークする。
   * @param item 選択したタグ
   */
  public toggleDeviceTagSelection(item: { [key: string]: string }) {
    if (this.selectedDeviceTag.includes(item)) {
      this.selectedDeviceTag = this.selectedDeviceTag.filter((i) => i !== item);
    } else {
      this.selectedDeviceTag.push(item);
    }
  }

  /**
   * 選択したタグ一覧エリアのタグを選択済みとしてマークする。
   * @param item 選択したタグ
   */
  public toggleTagSelection(item: { [key: string]: string }) {
    if (this.selectedTag.includes(item)) {
      this.selectedTag = this.selectedTag.filter((i) => i !== item);
    } else {
      this.selectedTag.push(item);
    }
  }

  /**
   * タグ名プロパティに基づいて昇順でソートする。
   * @param tagList ソート対象配列
   * @returns ソート済み配列
   */
  private sortTagsByTagName(
    tagList?: {
      [key: string]: string;
    }[],
  ) {
    if (!tagList) {
      return [];
    }
    return tagList.sort((a, b) => {
      if (a['tagName'] < b['tagName']) {
        return -1;
      } else if (a['tagName'] > b['tagName']) {
        return 1;
      } else {
        return 0;
      }
    });
  }
}
